import React, { createContext, useContext, useMemo, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const MAX_ID_KEY = 'max_id';
export const TG_ID_KEY = 'tg_id';
const IDENTITY_STORAGE_EVENT = 'messenger-identity-storage-updated';
const PLATFORM_KEY = 'messenger_platform';
const MAX_IDENTITY_POLL_INTERVAL_MS = 100;
const MAX_IDENTITY_POLL_TIMEOUT_MS = 10_000;

/**
 * Проверяет, является ли устройство iOS
 */
export function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Получить значение cookie по имени
 */
export function getCookieValue(name) {
    const matches = document.cookie.match(new RegExp(
        '(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'
    ));
    return matches ? decodeURIComponent(matches[1]) : null;
}

/**
 * Установить cookie с правильными флагами для iOS PWA
 * Согласно статье на Habr, cookies копируются при установке PWA на iOS,
 * в отличие от localStorage который НЕ копируется
 */
export function setCookie(name, value) {
    // Устанавливаем cookie на год с правильными флагами
    // SameSite=Lax - обязательно для работы с iOS PWA
    // path=/ - чтобы cookie была доступна на всех страницах
    document.cookie = `${name}=${value}; path=/; max-age=31536000; SameSite=Lax`;
    console.log(`🍪 Cookie set: ${name}=${value}`);
}

/** @returns {'telegram' | 'max' | 'web'} */
export function detectPlatform() {
    // return 'max';
    // return 'max';
    // return 'web';
    // TelegramWebviewProxy — нативный объект, который инжектит Telegram-клиент.
    // initData может быть пустой строкой при запуске с домашнего экрана,
    // поэтому проверяем наличие нативного прокси как надёжный признак Telegram.
    if (window.Telegram?.WebApp?.initData || window.TelegramWebviewProxy) return 'telegram';
    // Пустой MAX SDK существует и в браузере; нужны данные запуска или пользователя.
    if (window.WebApp?.initData || window.WebApp?.initDataUnsafe?.user?.id) return 'max';
    if (getMaxIdFromUrl()) return 'max';
    if (getTgIdFromUrl()) return 'telegram';
    const storedPlatform = getPlatformFromStorage();
    if (storedPlatform === 'max' && getMaxIdFromStorage()) return 'max';
    if (storedPlatform === 'telegram' && getTgIdFromStorage()) return 'telegram';
    // MAX-пользователь, открывший PWA из браузера — SDK нет
    if(getMaxIdFromUrl() || getMaxIdFromStorage()) return 'max';
    // Telegram-пользователь, открывший PWA из браузера — SDK нет
    if(getTgIdFromUrl() || getTgIdFromStorage()) return 'telegram';
    return 'web';
}

export function getMaxIdFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(MAX_ID_KEY);
}

export function saveMaxIdToStorage(maxId) {
    if (maxId) {
        const ios = isIOS();
        
        // На iOS ВСЕГДА сохраняем в cookies (они копируются при установке PWA)
        // На других платформах сохраняем в localStorage
        if (ios) {
            setCookie(MAX_ID_KEY, String(maxId));
            console.log(`📱 iOS detected: saved max_id to cookie (will be copied on PWA install)`);
        } else {
            localStorage.setItem(MAX_ID_KEY, String(maxId));
            setCookie(MAX_ID_KEY, String(maxId));
            console.log(`💾 Non-iOS: saved max_id to localStorage`);
        }
        localStorage.setItem(PLATFORM_KEY, 'max');
        setCookie(PLATFORM_KEY, 'max');
        window.dispatchEvent(new Event(IDENTITY_STORAGE_EVENT));
    }
}

export function getMaxIdFromStorage() {
    const ios = isIOS();
    
    // На iOS читаем из cookies (localStorage не копируется при установке PWA)
    if (ios) {
        const fromCookie = getCookieValue(MAX_ID_KEY);
        if (fromCookie) {
            console.log(`🍪 iOS: Retrieved max_id from cookie: ${fromCookie}`);
            return fromCookie;
        }
    }
    
    // Fallback на localStorage для других платформ или если cookie пустая
    const fromStorage = localStorage.getItem(MAX_ID_KEY);
    if (fromStorage) {
        console.log(`💾 Retrieved max_id from localStorage: ${fromStorage}`);
    }
    return fromStorage;
}

export function getTgIdFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(TG_ID_KEY);
}

export function saveTgIdToStorage(tgId) {
    if (tgId) {
        const ios = isIOS();
        
        // На iOS ВСЕГДА сохраняем в cookies (они копируются при установке PWA)
        // На других платформах сохраняем в localStorage
        if (ios) {
            setCookie(TG_ID_KEY, String(tgId));
            console.log(`📱 iOS detected: saved tg_id to cookie (will be copied on PWA install)`);
        } else {
            localStorage.setItem(TG_ID_KEY, String(tgId));
            setCookie(TG_ID_KEY, String(tgId));
            console.log(`💾 Non-iOS: saved tg_id to localStorage`);
        }
        localStorage.setItem(PLATFORM_KEY, 'telegram');
        setCookie(PLATFORM_KEY, 'telegram');
        window.dispatchEvent(new Event(IDENTITY_STORAGE_EVENT));
    }
}

export function getTgIdFromStorage() {
    const ios = isIOS();
    
    // На iOS читаем из cookies (localStorage не копируется при установке PWA)
    if (ios) {
        const fromCookie = getCookieValue(TG_ID_KEY);
        if (fromCookie) {
            console.log(`🍪 iOS: Retrieved tg_id from cookie: ${fromCookie}`);
            return fromCookie;
        }
    }
    
    // Fallback на localStorage для других платформ или если cookie пустая
    const fromStorage = localStorage.getItem(TG_ID_KEY);
    if (fromStorage) {
        console.log(`💾 Retrieved tg_id from localStorage: ${fromStorage}`);
    }
    return fromStorage;
}

function getPlatformFromStorage() {
    return getCookieValue(PLATFORM_KEY) || localStorage.getItem(PLATFORM_KEY);
}

function mapUser(raw) {
    if (!raw) return null;
    return {
        id: raw.id,
        firstName: raw.first_name,
        lastName: raw.last_name,
        username: raw.username,
        languageCode: raw.language_code,
        photoUrl: raw.photo_url ?? null,
    };
}

const MessengerContext = createContext(null);

export const useMessenger = () => {
    const ctx = useContext(MessengerContext);
    if (!ctx) {
        throw new Error('useMessenger must be used within MessengerProvider');
    }
    return ctx;
};

export const MessengerProvider = ({ children }) => {
    const backButtonHandlerRef = useRef(null);
    const navigate = useNavigate();
    const [identityVersion, setIdentityVersion] = useState(0);

    useEffect(() => {
        const onIdentityStorageUpdated = () => setIdentityVersion((version) => version + 1);
        window.addEventListener(IDENTITY_STORAGE_EVENT, onIdentityStorageUpdated);

        // При монтировании провайдера проверяем URL на наличие max_id / tg_id и сохраняем
        const maxIdFromUrl = getMaxIdFromUrl();
        if (maxIdFromUrl) {
            saveMaxIdToStorage(maxIdFromUrl);
        }
        const tgIdFromUrl = getTgIdFromUrl();
        if (tgIdFromUrl) {
            saveTgIdToStorage(tgIdFromUrl);
        }

        return () => window.removeEventListener(IDENTITY_STORAGE_EVENT, onIdentityStorageUpdated);
    }, []);

    useEffect(() => {
        // MAX может создать WebApp и заполнить initDataUnsafe уже после первого
        // рендера React. Контекст в этот момент запоминает userId как undefined,
        // поэтому дождёмся появления идентичности и пересчитаем его один раз.
        if (window.WebApp?.initDataUnsafe?.user?.id) {
            return undefined;
        }

        const refreshIdentity = () => {
            if (!window.WebApp?.initDataUnsafe?.user?.id) {
                return false;
            }

            setIdentityVersion((version) => version + 1);
            return true;
        };

        if (refreshIdentity()) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            if (refreshIdentity()) {
                window.clearInterval(intervalId);
            }
        }, MAX_IDENTITY_POLL_INTERVAL_MS);
        const timeoutId = window.setTimeout(
            () => window.clearInterval(intervalId),
            MAX_IDENTITY_POLL_TIMEOUT_MS,
        );

        return () => {
            window.clearInterval(intervalId);
            window.clearTimeout(timeoutId);
        };
    }, []);

    const value = useMemo(() => {
        const platform = detectPlatform();

        let webapp = null;
        if (platform === 'telegram') {
            webapp = window.Telegram?.WebApp ?? null;
        } else if (platform === 'max') {
            webapp = window.WebApp ?? null;
        }

        const initDataUnsafe = webapp?.initDataUnsafe ?? {};
        const initData = webapp?.initData ?? '';
        const nativePlatform = webapp?.platform ?? 'web';

        // Для MAX сначала пробуем взять из initDataUnsafe, затем из URL, затем из хранилища
        let userId = initDataUnsafe?.user?.id;
        if (platform === 'max' && !userId) {
            userId = getMaxIdFromUrl() || getMaxIdFromStorage();
        }
        // Проверяем tg_id из URL/хранилища (пользователь пришёл из Telegram через внешний браузер)
        if ((platform === 'web' || platform === 'telegram') && !userId) {
            userId = getTgIdFromUrl() || getTgIdFromStorage();
        }
        // Fallback для разработки
        // if (!userId) {
        //     userId = 321611257;
        // }
        
        const user = mapUser(initDataUnsafe?.user);

        const close = () => {
            if ((platform === 'telegram' || platform === 'max') && webapp) {
                webapp.close();
            } else {
                window.close();
            }
        };

        const goToMenu = () => {
            navigate('/menu');
        };

        const expand = () => {
            if (platform === 'telegram' && webapp) {
                webapp.expand();
            }
        };

        const openLink = (url) => {
            let finalUrl = url;

            // Для MAX добавляем max_id, для Telegram — tg_id в query параметры
            if ((platform === 'max' || platform === 'telegram') && (userId || initData)) {
                try {
                    const urlObj = new URL(url, window.location.origin);
                    const key = platform === 'max' ? MAX_ID_KEY : TG_ID_KEY;
                    if (userId) {
                        urlObj.searchParams.set(key, String(userId));
                    }
                    if (initData) {
                        urlObj.searchParams.set('platform', platform);
                        urlObj.searchParams.set('platform_init_data', initData);
                    }
                    finalUrl = urlObj.toString();
                } catch (e) {
                    console.error('Error adding user ID to URL:', e);
                }
            }

            if (webapp?.openLink) {
                webapp.openLink(finalUrl);
            } else {
                window.open(finalUrl, '_blank');
            }
        };

        const showBackButton = (onClick) => {
            if (!webapp?.BackButton) return;

            // Снимаем предыдущий обработчик из ref перед регистрацией нового.
            // Иначе при двойной регистрации (например страница с кастомным back
            // override-ит глобальный AppBackButton) старые обработчики остаются
            // в SDK и при тапе "Назад" срабатывают все накопленные сразу.
            const prev = backButtonHandlerRef.current;
            if (prev && (platform === 'telegram' || platform === 'max')) {
                webapp.BackButton.offClick(prev);
            }

            webapp.BackButton.show();
            if (platform === 'telegram' || platform === 'max') {
                webapp.BackButton.onClick(onClick);
            }
            backButtonHandlerRef.current = onClick;
        };

        const hideBackButton = () => {
            if (!webapp?.BackButton) return;
            webapp.BackButton.hide();
            const handler = backButtonHandlerRef.current;
            if (handler && (platform === 'telegram' || platform === 'max')) {
                // Симметрия с onClick - в MAX SDK BackButton.offClick тоже
                // существует и работает с тем же внутренним списком, что и onClick.
                // Раньше тут стоял webapp.offEvent('back_button_pressed', ...),
                // который смотрит в другой список и не находил обработчик.
                webapp.BackButton.offClick(handler);
            }
            backButtonHandlerRef.current = null;
        };

        return {
            platform,
            nativePlatform,
            userId,
            user,
            initData,
            initDataUnsafe,
            navigate,
            close,
            expand,
            goToMenu,
            openLink,
            showBackButton,
            hideBackButton,
        };
    // identityVersion intentionally invalidates the memo after install handoff storage updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, identityVersion]);

    useEffect(() => {
        const { platform } = value;
        if (platform === 'telegram') {
            // ready() нужно вызывать как можно раньше — без этого
            // Telegram-клиент может закрыть мини-приложение по таймауту
            // (особенно актуально при запуске с иконки на рабочем столе)
            window.Telegram?.WebApp?.ready?.();
            value.expand();
        }
    }, [value]);

    return (
        <MessengerContext.Provider value={value}>
            {children}
        </MessengerContext.Provider>
    );
};
